import { CADENCE_PRESETS } from '@forwardflow/features/campaign-flow/data/mock'
import {
   IConversationDefinition,
   IPhoneDefinition,
   IStrategyDefinition,
} from '@forwardflow/features/campaign-v2/interfaces/campaign-v2.types'
import { IPlaybook } from '@forwardflow/features/campaign-flow/interfaces/campaign-flow.types'
import { syncMessages } from '@forwardflow/features/campaign-flow/utils/playbook'
import { fromTemplate } from '@forwardflow/features/campaign-flow/utils/text'

/**
 * Library content → the playbook the screens edit, word for word: the
 * opening line, every section in order (Forward Flow's fixed ones marked
 * protected), every text and voicemail. The backend has no recommended
 * cadence, so timing starts from the balanced preset.
 */
export interface IContentSnapshot {
   phone: { name: string; definition: IPhoneDefinition } | null
   strategy: { name: string; definition: IStrategyDefinition } | null
   conversation: { name: string; definition: IConversationDefinition } | null
}

/** A library call script, word for word: its opening line and every section in order. */
export const scriptFromPhone = (def: IPhoneDefinition) => ({
   opening: fromTemplate(def.firstMessage || ''),
   script: [...(def.sections || [])]
      .sort((a, b) => a.order - b.order)
      .map((s, i) => ({
         id: String(s.sectionId ?? `s${i + 1}`),
         title: s.name,
         /* A fixed section is shown and written back exactly as the library has it. */
         text: s.protected ? s.content : fromTemplate(s.content),
         ...(s.sectionId !== undefined ? { sectionId: s.sectionId } : {}),
         ...(s.type ? { type: s.type } : {}),
         ...(s.templateType ? { templateType: s.templateType } : {}),
         ...(s.protected ? { protected: true } : {}),
      })),
})

/** A library message group: every text and voicemail, in order. */
export const messagesFromStrategy = (def: IStrategyDefinition) => ({
   texts: (def.sms || []).map((m, i) => ({
      id: `s${i + 1}`,
      text: fromTemplate(m.template),
   })),
   voicemails: (def.voicemail || []).map((m, i) => ({
      id: `v${i + 1}`,
      text: fromTemplate(m.template),
   })),
})

export const playbookFromContent = (
   c: IContentSnapshot,
   fallback: IPlaybook
): IPlaybook => {
   const phone = c.phone ? scriptFromPhone(c.phone.definition) : null
   const script = phone?.script.length ? phone.script : fallback.script
   const opening = phone ? phone.opening : (fallback.opening ?? '')
   const sms = c.strategy?.definition.sms || []
   const vms = c.strategy?.definition.voicemail || []
   return syncMessages({
      ...fallback,
      origin: 'recommended',
      scriptName: c.phone?.name || fallback.scriptName,
      messageGroupName: c.strategy?.name || fallback.messageGroupName,
      preset: 'balanced',
      touches: CADENCE_PRESETS.balanced.touches.map((t) => ({ ...t })),
      opening,
      script,
      texts: sms.length
         ? sms.map((m, i) => ({
              id: `s${i + 1}`,
              text: fromTemplate(m.template),
           }))
         : fallback.texts,
      voicemails: vms.length
         ? vms.map((m, i) => ({
              id: `v${i + 1}`,
              text: fromTemplate(m.template),
           }))
         : fallback.voicemails,
      replyInstructions:
         c.conversation?.definition.content || fallback.replyInstructions,
   })
}

/** What the content calls care about. Timing lives in the configuration, not in content. */
export const contentHash = (pb: IPlaybook) =>
   JSON.stringify({
      o: pb.opening ?? '',
      s: pb.script.map((x) => [x.title, x.text, x.type ?? 'call_outbound']),
      t: pb.texts.map((x) => x.text),
      v: pb.voicemails.map((x) => x.text),
      r: pb.replyInstructions,
   })
